import axios from 'axios';
import { deleteDocumentsMutation } from '@/api/contract-management/graphql/contract-management.mutations';
import { apolloClient } from '@/apollo';
import { applyTimezoneOffset } from '@/common/timezone/timezone.helper';
import { IUpdateDocument } from '@/dealroom/document/types/update-document';
import { ERecipientRole } from '@/dealroom/recipients/types/enums/recipient-role';
import { IUpdateDocumentPageInput } from '@/dealroom/types/update-document-page-input';
import {
  addDocumentPageMutation,
  createRecipientMutation,
  deleteRecipientMutation,
  getUploadFilesUrlsMutation,
  removeDocumentAttachmentMutation,
  removeDocumentPageMutation,
  sealDocumentMutation,
  sortDocumentPagesMutation,
  updateDocumentAttachmentMutation,
  updateDocumentFromTemplateMutation,
  updateDocumentMutation,
  updateDocumentPageMutation,
  updateRecipientMutation,
  updateRecipientOrderMutation,
  updateSignatureMutation,
  uploadFilesDoneMutation,
} from '@/graphql/mutations/dealroom.mutations';
import { documentAttachments, recipientsQuery } from '@/graphql/queries/dealroom.queries';
import { IDocument } from '@/types/Document';
import { IDocumentAttachment } from '@/types/document-attachment';
import { IDocumentPage } from '@/types/document-page';
import { IRecipient } from '@/types/Recipient';
import { IUploadFileUrl } from '@/types/upload-file-url';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';

const updateDocumentPage = async (
  documentPage: IUpdateDocumentPageInput
): Promise<IDocumentPage> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentPageMutation,
    variables: documentPage,
  });
  return data.updateDocumentPage;
};

const createVideoDocumentPage = async (
  documentId: string,
  videoId: string,
  pageNum: number
): Promise<IDocumentPage> => {
  const {
    data: { addDocumentPage },
  } = await apolloClient.mutate({
    mutation: addDocumentPageMutation,
    variables: { documentId, videoId, pageNum, pageType: 'video' },
  });
  return addDocumentPage;
};

const createEditorBlock = async (documentId: string, pageNum: number): Promise<IDocumentPage> => {
  const {
    data: { addDocumentPage },
  } = await apolloClient.mutate({
    mutation: addDocumentPageMutation,
    variables: { documentId, pageNum, pageType: 'editorBlock' },
  });
  return addDocumentPage;
};

const deleteDocumentPage = async (documentPageId: string): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: removeDocumentPageMutation,
    variables: { id: documentPageId },
  });
  return data;
};

const getUploadFilesUrls = async (
  documentId: string,
  filenames: string[]
): Promise<IUploadFileUrl[]> => {
  const { data } = await apolloClient.mutate({
    mutation: getUploadFilesUrlsMutation,
    variables: { documentId, filenames },
  });
  return data.getUploadFilesUrls;
};

const uploadFiles = async (uploadFileUrls: IUploadFileUrl[], files: File[]): Promise<void> => {
  await Promise.all(
    uploadFileUrls.map(async ({ uploadUrl }: IUploadFileUrl, index: number) => {
      return axios.put(uploadUrl, files[index]);
    })
  );
};

const uploadFilesDone = async (
  documentId: string,
  index: number,
  uniqueFilenames: string[]
): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: uploadFilesDoneMutation,
    variables: { documentId, index, uniqueFilenames },
  });
  return data.uploadFilesDone;
};

const updateDocument = async (payload: IUpdateDocument, documentId: string): Promise<string> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentMutation,
    variables: { ...correctDatesUpdate(payload), documentId },
  });
  return data.updateDocument.id;
};

export async function saveRecipient(recipient: IRecipient, documentId: string): Promise<string> {
  const body: FormData = new FormData();
  body.append('action', 'save');
  body.append('recipient-id', recipient.id || '');
  body.append('recipient-first_name', recipient.firstName);
  body.append('recipient-last_name', recipient.lastName || '');
  body.append('recipient-role', recipient.role);
  body.append('recipient-email', recipient.email || '');
  body.append('recipient-company_name', recipient.companyName || '');
  body.append('recipient-company_number', recipient.companyNumber || '');
  body.append('recipient-title', recipient.title || '');
  body.append('recipient-mobile', recipient.mobile || '');
  body.append('recipient-note', recipient.note || '');
  body.append('recipient-thumb_url', recipient.thumbUrl || '');
  body.append('verify-eid-type', 'bankid');
  body.append('document-id', documentId);

  const { data } = await axios.post(`/save/recipient`, body);
  if (data.status === 1 || data.status === 2) {
    return data.recipient_id;
  }
  throw new Error(data.error);
}

export async function updateRecipientThumbnail(file: File, email: string): Promise<string> {
  const body: FormData = new FormData();
  body.append('file', file);
  body.append('email', email || '');

  const { data } = await axios.post(`/save/recipient`, body);
  if (data.success) {
    const newThumbnailTimestamp = Math.round(new Date().getTime() / 1000) - 1;
    return `${data.image_url}?${newThumbnailTimestamp}`;
  }
  throw new Error(data.error);
}

const createRecipient = async (
  contactId: string,
  documentId: string,
  role: ERecipientRole
): Promise<IRecipient> => {
  const { data } = await apolloClient.mutate({
    mutation: createRecipientMutation,
    variables: { documentId, contactId, role },
  });
  return data.createRecipient;
};

const addRecipient = async (payload: IRecipient): Promise<string> => {
  const body: FormData = new FormData();
  body.append('action', 'save');
  body.append('recipient-role_name', payload.roleName);
  body.append('recipient-role', payload.role);
  body.append('document-id', payload.documentId);
  body.append('verify-eid-type', 'bankid');
  body.append('verify-eid', payload.verifyEid.toString());
  body.append('verify-sms', payload.verifySms.toString());
  body.append('verify-sms-open', payload.verifySmsOpen.toString());
  body.append('verify-sms-sign', payload.verifySmsSign.toString());
  if (payload.recipientId) {
    body.append('recipient-id', payload.recipientId);
  }

  const { data } = await axios.post('/save/recipient', body);
  if (data.status === 1 || data.status === 2) {
    return data.recipient_id;
  }
  throw new Error(data.error);
};

const deleteRecipient = async (id: string): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: deleteRecipientMutation,
    variables: { id },
  });
  return !!data;
};

const updateRecipient = async (id: string, properties: Partial<IRecipient>) => {
  const { data } = await apolloClient.mutate({
    mutation: updateRecipientMutation,
    variables: { id, ...properties },
  });
  return data;
};

const attachmentRequireView = async (documentAttachment: IDocumentAttachment): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentAttachmentMutation,
    variables: {
      id: documentAttachment.id,
      requireView: documentAttachment.requireView,
    },
  });
  return !!data;
};

const attachmentRestrictAccess = async (
  documentAttachment: IDocumentAttachment,
  recipientIds: string[]
): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentAttachmentMutation,
    variables: {
      id: documentAttachment.id,
      accessRecipientList: recipientIds,
    },
  });
  return !!data;
};

const attachmentRemoveRestrictedAccess = async (
  documentAttachment: IDocumentAttachment
): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentAttachmentMutation,
    variables: {
      id: documentAttachment.id,
      accessRecipientList: null,
    },
  });
  return !!data;
};

const deleteAttachment = async (documentAttachment: IDocumentAttachment): Promise<boolean> => {
  const { data } = await apolloClient.mutate({
    mutation: removeDocumentAttachmentMutation,
    variables: {
      id: documentAttachment.id,
    },
  });
  return data;
};

const loadDocumentAttachments = async (documentId: string): Promise<IDocumentAttachment[]> => {
  const {
    data: { document },
  } = await apolloClient.query({
    query: documentAttachments,
    variables: { documentId },
    fetchPolicy: EFetchPolicy.NetworkOnly,
  });

  return document.documentAttachments;
};

const loadRecipients = async (documentId: string): Promise<IRecipient[]> => {
  const {
    data: { document },
  } = await apolloClient.query({
    query: recipientsQuery,
    variables: { documentId },
    fetchPolicy: EFetchPolicy.NetworkOnly,
  });
  return document.recipients;
};

const useTemplate = async (
  documentId: string,
  templateId: string,
  pageOffset: number
): Promise<IDocument> => {
  const {
    data: { updateDocumentFromTemplate },
  } = await apolloClient.mutate({
    mutation: updateDocumentFromTemplateMutation,
    variables: {
      documentId,
      templateId,
      pageOffset,
    },
  });
  return updateDocumentFromTemplate;
};

const deleteDocuments = async (ids: [string]): Promise<IDocument> => {
  const { data } = await apolloClient.mutate({
    mutation: deleteDocumentsMutation,
    variables: {
      ids,
    },
  });
  return data;
};

const sortDocumentPages = async (
  documentId: string,
  documentPageIds: string[],
  pageNums: number[]
): Promise<IDocumentPage[]> => {
  const { data } = await apolloClient.mutate({
    mutation: sortDocumentPagesMutation,
    variables: {
      documentId,
      documentPageIds,
      pageNums,
    },
  });
  return data.sortDocumentPages;
};

const updateRecipientOrder = async (documentId: string, recipientOrder: Partial<IRecipient>[]) => {
  const { data } = await apolloClient.mutate({
    mutation: updateRecipientOrderMutation,
    variables: {
      documentId,
      recipientOrder,
    },
  });
  return data;
};

export function correctDates(document: IDocument): IDocument {
  if (!document) {
    return null;
  }
  return {
    ...document,
    expirationDate: applyTimezoneOffset(document.expirationDate, true),
    contractEndDate: applyTimezoneOffset(document.contractEndDate, true),
    contractStartDate: applyTimezoneOffset(document.contractStartDate, true),
  };
}

export function correctDatesUpdate(payload: IUpdateDocument): IUpdateDocument {
  const res: IUpdateDocument = payload;
  if (res.contractStartDate) {
    res.contractStartDate = applyTimezoneOffset(payload.contractStartDate, false);
  }
  if (res.contractEndDate) {
    res.contractEndDate = applyTimezoneOffset(payload.contractEndDate, false);
  }
  if (res.expirationDate) {
    res.expirationDate = applyTimezoneOffset(payload.expirationDate, false);
  }
  return res;
}

const sealDocument = async (documentId: string, sendDocument: boolean): Promise<void> => {
  await apolloClient.mutate({
    mutation: sealDocumentMutation,
    variables: {
      documentId,
      sendDocument,
    },
  });
};

export async function deleteTemplate(id: string): Promise<void> {
  const body: FormData = new FormData();
  body.append('action', 'delete');
  body.append('document-id', id);
  await axios.post('/save/board', body);
}

const updateSignature = async (signature: string): Promise<void> => {
  await apolloClient.mutate({
    mutation: updateSignatureMutation,
    variables: {
      signature,
    },
  });
};

export async function assignFieldToRecipient(
  documentId: string,
  recipientId: string
): Promise<void> {
  const body: FormData = new FormData();
  body.append('action', 'assign-fields');
  body.append('document-id', documentId);
  body.append('recipient-id', recipientId);
  await axios.post('/save/document', body);
}

export default {
  createVideoDocumentPage,
  createEditorBlock,
  createRecipient,
  updateRecipientThumbnail,
  saveRecipient,
  deleteDocumentPage,
  loadRecipients,
  updateDocumentPage,
  getUploadFilesUrls,
  uploadFiles,
  uploadFilesDone,
  updateDocument,
  deleteRecipient,
  updateRecipient,
  attachmentRequireView,
  deleteAttachment,
  attachmentRestrictAccess,
  attachmentRemoveRestrictedAccess,
  loadDocumentAttachments,
  useTemplate,
  deleteDocuments,
  sortDocumentPages,
  updateRecipientOrder,
  sealDocument,
  deleteTemplate,
  updateSignature,
  assignFieldToRecipient,
  addRecipient,
};
